/**
 * jrn-ui — shared primitives for the ported Journeys feature: the icon set,
 * Modal, Field, OpsToggleRow, BrandColorPicker, FontSelect,
 * SectionHead. ADAPTATION (cross-doc port conformance rule e): the source
 * prototype's generic UI atoms are re-authored here to the same visual intent
 * on jrni tokens rather than transcribed from its global ui/dialogs files —
 * the *feature* is the Journeys screens, not these atoms.
 */
import { useState, type CSSProperties, type ReactNode, type ChangeEvent } from "react";
import { Fonts } from "./journey-fonts";

import { useT } from "@v0y4ge/i18n-runtime";
import { T, Btn, Toggle, Input } from "@v0y4ge/jrni-ui";
/* ── Icons ─────────────────────────────────────────────────────────── */
type IconProps = { size?: number; style?: CSSProperties };
const svg = (path: ReactNode) => ({ size = 14, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
    strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, display: "inline-block", verticalAlign: "middle", ...style }} aria-hidden="true">{path}</svg>
);
export const I = {
  Plus: svg(<><path d="M12 5v14M5 12h14" /></>),
  X: svg(<><path d="M6 6l12 12M18 6L6 18" /></>),
  Check: svg(<><path d="M20 6L9 17l-5-5" /></>),
  Pencil: svg(<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>),
  Browser: svg(<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /></>),
  Edit: svg(<><path d="M4 20h16" /><path d="M14.5 4.5l5 5L8 21l-5 1 1-5Z" /></>),
  Calendar: svg(<><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></>),
  Clock: svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  ChevronL: svg(<><path d="M15 6l-6 6 6 6" /></>),
  ChevronR: svg(<><path d="M9 6l6 6-6 6" /></>),
  Undo: svg(<><path d="M9 14L4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" /></>),
  Sparkle: svg(<><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8Z" /></>),
  Warning: svg(<><path d="M12 3l10 17H2Z" /><path d="M12 10v4M12 17.5v.5" /></>),
  Friends: svg(<><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M16 6a3 3 0 0 1 0 6M17 20a6 6 0 0 0-3-5.2" /></>),
  Network: svg(<><circle cx="6" cy="18" r="2.5" /><circle cx="18" cy="18" r="2.5" /><circle cx="12" cy="6" r="2.5" /><path d="M12 8.5v3M10 15l-2-4M14 15l2-4" /></>),
  Tags: svg(<><path d="M3 8v5l8 8 8-8-8-8H8Z" /><circle cx="8" cy="9" r="1.3" /></>),
  User: svg(<><circle cx="12" cy="8" r="3.5" /><path d="M4 21a8 8 0 0 1 16 0" /></>),
  List: svg(<><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></>),
  Video: svg(<><rect x="2.5" y="6" width="13" height="12" rx="2" /><path d="M15.5 10l6-3v10l-6-3Z" /></>),
  Phone: svg(<><path d="M4 5c0 8 7 15 15 15l-1.5-4-4 1a12 12 0 0 1-6.5-6.5l1-4Z" /></>),
  Bell: svg(<><path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" /><path d="M10 20a2 2 0 0 0 4 0" /></>),
};

const NAME_MAP: Record<string, keyof typeof I> = {
  pencil: "Pencil", browser: "Browser", "network-wired": "Network", sparkle: "Sparkle",
  tags: "Tags", calendar: "Calendar", "user-friends": "Friends", clock: "Clock",
  user: "User", check: "Check", edit: "Edit", list: "List",
};
export function Icon({ name, size = 14, style }: { name: string; size?: number; style?: CSSProperties }) {
  const Cmp = I[NAME_MAP[name] || "List"];
  return <Cmp size={size} style={style} />;
}

/* Custom glyphs used by the list + editor (verbatim from the source). */
export const JrnTrash = svg(<><path d="M4 7h16M9 7V5.2A2.2 2.2 0 0 1 11.2 3h1.6A2.2 2.2 0 0 1 15 5.2V7M6.5 7l.9 12.1A2 2 0 0 0 9.4 21h5.2a2 2 0 0 0 2-1.9L17.5 7" /></>);
export const JrnEyeOff = svg(<><path d="M3 3l18 18M10.7 5.2A10 10 0 0 1 12 5.1c5 0 8.6 3.8 9.8 6.3.2.4.2.8 0 1.2a13.2 13.2 0 0 1-3.1 3.9M6.3 6.3a12.7 12.7 0 0 0-4.1 5.1c-.2.4-.2.8 0 1.2 1.2 2.5 4.8 6.3 9.8 6.3 1.6 0 3-.4 4.3-1M9.9 9.9a3 3 0 0 0 4.2 4.2" /></>);

/* ── SectionHead ───────────────────────────────────────────────────── */
export function SectionHead({ title, subtitle, right }: { title: ReactNode; subtitle?: ReactNode; right?: ReactNode }) {
  return (
    <div className="section-head">
      <div className="col" style={{ gap: 2 }}>
        <div className="card-title">{title}</div>
        {subtitle && <div className="card-subtitle">{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

/* ── Field ─────────────────────────────────────────────────────────── */
export function Field({ label, children, flex }: { label?: ReactNode; children: ReactNode; flex?: boolean }) {
  return (
    <label className="col" style={{ gap: 6, flex: flex ? 1 : undefined, minWidth: flex ? 0 : undefined }}>
      {label != null && <span style={{ fontSize: 12, fontWeight: 700, color: "var(--jrni-color-text-strong)" }}>{label}</span>}
      {children}
    </label>
  );
}


/* ── OpsToggleRow ──────────────────────────────────────────────────── */
export function OpsToggleRow({ label, sub, value, onChange, disabled }: { label: ReactNode; sub?: ReactNode; value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div className="row" style={{ gap: 12, justifyContent: "space-between", alignItems: "flex-start", padding: "2px 0" }}>
      <div className="col" style={{ gap: 1, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--jrni-color-text-strong)" }}>{label}</span>
        {sub && <span style={{ fontSize: 11, color: "var(--jrni-color-text-soft)", lineHeight: 1.5, textWrap: "pretty" }}>{sub}</span>}
      </div>
      <Toggle on={value} onChange={onChange} disabled={disabled} />
    </div>
  );
}

/* ── BrandColorPicker ──────────────────────────────────────────────── */
export function BrandColorPicker({ value, onChange, swatches }: { value: string; onChange: (v: string) => void; swatches?: string[] }) {
  return (
    <div className="col" style={{ gap: 8 }}>
      <span className="row" style={{ gap: 8, alignItems: "center" }}>
        {/* ui-raw: native OS colour picker — no like-for-like jrni-ui equivalent (ColorPicker is a full sat/val canvas) */}
        <input type="color" value={value} onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)} style={{ width: 36, height: 30, padding: 2, border: "1px solid var(--jrni-color-neutral-5)", borderRadius: 6, background: T.white, cursor: "pointer" }} />
        <Input size="sm" full={false} value={value} onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)} style={{ width: 110 }} />
      </span>
      {swatches && (
        <span className="row" style={{ gap: 6, flexWrap: "wrap" }}>
          {swatches.map((s) => (
            // ui-raw: bespoke colour swatch chip — the fill IS the colour value, with a selected ring; not a Btn/Chip role
            <button key={s} type="button" title={s} onClick={() => onChange(s)} style={{ width: 22, height: 22, borderRadius: 6, cursor: "pointer", padding: 0, background: s, border: value.toLowerCase() === s.toLowerCase() ? "2px solid var(--jrni-color-primary-1)" : "1px solid var(--jrni-color-neutral-4)" }} />
          ))}
        </span>
      )}
    </div>
  );
}

/* ── FontSelect ────────────────────────────────────────────────────── */
export function FontSelect({ value, onChange, sample }: { value: string; onChange: (v: string) => void; sample?: string }) {
  return (
    <div className="col" style={{ gap: 6 }}>
      {/* ui-raw: native grouped <optgroup> font picker with a Fonts.ensure() load side-effect — no jrni-ui equivalent (Dropdown has no optgroups) */}
      <select className="select" value={value} onChange={(e: ChangeEvent<HTMLSelectElement>) => { Fonts.ensure(e.target.value); onChange(e.target.value); }}>
        {Fonts.groups().map((g) => (
          <optgroup key={g.cat} label={g.cat}>
            {g.items.map((f) => <option key={f.id} value={f.id}>{f.id}</option>)}
          </optgroup>
        ))}
      </select>
      {sample && <span style={{ fontFamily: Fonts.stack(value), fontSize: 15, color: "var(--jrni-color-text-strong)" }}>{sample}</span>}
    </div>
  );
}

/* ── Modal ─────────────────────────────────────────────────────────── */
export function Modal({ title, subtitle, width = 560, onClose, footer, children }: { title: ReactNode; subtitle?: ReactNode; width?: number; onClose: () => void; footer?: ReactNode; children: ReactNode }) {
  const t = useT();
  return (
    <div className="jrn-scope" onClick={onClose} role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(20,22,30,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width, maxWidth: "94vw", maxHeight: "88vh", background: "var(--jrni-color-surface-card)", borderRadius: 14, boxShadow: "var(--jrni-shadow-lg, 0 24px 60px rgba(20,22,30,0.28))", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div className="row" style={{ padding: "16px 20px", borderBottom: "1px solid var(--jrni-color-neutral-5)", gap: 10, alignItems: "flex-start" }}>
          <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: "var(--jrni-color-text-strong)" }}>{title}</span>
            {subtitle && <span style={{ fontSize: 12, color: "var(--jrni-color-text-soft)" }}>{subtitle}</span>}
          </div>
          <Btn variant="ghost" size="sm" iconOnly={<I.X size={14} />} onClick={onClose} aria-label={t('journeys.jrnUi.ariaLabel')} />
        </div>
        <div style={{ padding: "16px 20px", overflowY: "auto" }}>{children}</div>
        {footer && <div style={{ padding: "12px 20px", borderTop: "1px solid var(--jrni-color-neutral-5)", display: "flex" }}>{footer}</div>}
      </div>
    </div>
  );
}
